import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Loader2, Mic, MicOff } from 'lucide-react';
import { useProfile } from '../services/profile';
import { useVoiceRecorder, withTranscript } from '../services/voiceRecorder';
import { getSession } from '../services/supabase';
import { useAuthStore } from '../stores/authStore';
import { dictationRecordingPurpose } from '@cerebro-diem/core';
import { iconButton, inputClass } from './styles';

/**
 * A labeled textarea with a hold-to-record button: what is said lands at the
 * cursor, or on a new line after the text when no cursor was placed. It only
 * changes the field; saving is still the form's Save.
 */
export default function DictationTextarea({
  label,
  recordingKey,
  value,
  onChange,
  rows,
  autoFocus = false,
  disabled = false,
}: {
  label: string;
  /** Stable destination identity; a different field must never inherit this raw audio. */
  recordingKey?: string;
  value: string;
  onChange: (value: string) => void;
  rows: number;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const id = useId();
  const [fieldInstance] = useState(() => crypto.randomUUID());
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Transcription finishes after the person may have typed more; add to what is there then.
  const valueRef = useRef(value);
  valueRef.current = value;
  // Whether the person has put the cursor somewhere (clicking, typing, arrow keys). Focus alone does not count.
  const cursorPlacedRef = useRef(false);
  const pendingCursorRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The person's tap-or-hold setting; holding until it is known (CER-175).
  const voiceMode = useProfile().data?.voice_mode ?? 'push_to_talk';
  const recorder = useVoiceRecorder({
    recoveryKey: dictationRecordingPurpose(fieldInstance, recordingKey),
    onStart: () => setError(null),
    onError: (reason) => setError(`Nothing was added. ${reason}`),
    onTranscript: async (transcript, ownerId) => {
      const { data: { session } } = await getSession();
      if (!alive.current) return false;
      if (!session || session.user.id !== ownerId || useAuthStore.getState().user?.id !== ownerId) { setError('The signed-in account changed; the transcript was not added.'); return false; }
      if (disabled) return false;
      const el = textareaRef.current;
      const at = cursorPlacedRef.current && el ? { start: el.selectionStart, end: el.selectionEnd } : null;
      const next = withTranscript(valueRef.current, transcript, at);
      // Every dictated word stays: nothing here cuts what was said.
      pendingCursorRef.current = next.cursor;
      onChange(next.text);
      return true;
    },
  }, voiceMode);
  const { isRecording, isTranscribing } = recorder;

  // After dictating, the cursor sits just after the words, ready for the next ones.
  useLayoutEffect(() => {
    const cursor = pendingCursorRef.current;
    if (cursor === null) return;
    pendingCursorRef.current = null;
    textareaRef.current?.setSelectionRange(cursor, cursor);
  }, [value]);

  const placeCursor = () => {
    cursorPlacedRef.current = true;
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-xs font-medium text-on-surface-variant">
          {label}
        </label>
        <button
          type="button"
          {...recorder.pressHandlers}
          disabled={disabled || isTranscribing}
          aria-label={isRecording ? `${recorder.words.stop} dictating` : `${recorder.words.verb} to dictate ${label.toLowerCase()}`}
          className={`${iconButton} p-1.5 rounded-full transition-colors touch-none select-none disabled:opacity-50 disabled:cursor-not-allowed ${
            isRecording ? 'bg-error/10 text-error animate-pulse' : 'text-primary hover:bg-primary-container'
          }`}
        >
          {isTranscribing ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : isRecording ? (
            <MicOff className="w-4 h-4" aria-hidden="true" />
          ) : (
            <Mic className="w-4 h-4" aria-hidden="true" />
          )}
        </button>
      </div>
      <textarea
        id={id}
        ref={textareaRef}
        rows={rows}
        autoFocus={autoFocus}
        disabled={disabled}
        className={inputClass}
        value={value}
        onChange={(e) => {
          placeCursor();
          onChange(e.target.value);
        }}
        onMouseUp={placeCursor}
        onKeyUp={(e) => {
          if (e.key !== 'Tab' && e.key !== 'Shift') placeCursor();
        }}
      />
      {isRecording && (
        <p role="status" className="text-xs text-on-surface-variant animate-pulse">
          {recorder.words.recording}
        </p>
      )}
      {isTranscribing && (
        <p role="status" className="text-xs text-on-surface-variant">
          Transcribing…
        </p>
      )}
      {recorder.recoveryCount > 0 && <button type="button" disabled={disabled || isTranscribing} onClick={() => void recorder.retry()}>Retry kept recording</button>}
      {error && (
        <p role="alert" className="text-xs text-error">
          {error}
        </p>
      )}
    </div>
  );
}
